import { ChangeDetectorRef, Directive, ElementRef, forwardRef, HostListener, Inject, Input, Renderer2 } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { EventBus, EventBusBase } from '@skylab-angular-shared/core';
import { Keys } from '@skylab-shared/ui';
import { BrowserIdentifier } from '@skylab-shared/utils';
import { ValueAccessor } from './value-accessor';

@Directive({
    selector: 'ins-textarea',
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => TextareaValueAccessorDirective),
            multi: true,
        },
    ]
})
export class TextareaValueAccessorDirective extends ValueAccessor {
    @Input() public rowsLimit = 3;
    @Input() public multiline = false;

    public currentRowsCount = 1;

    public constructor(
        elm: ElementRef,
        @Inject(EventBus) eventBus: EventBusBase,
        private readonly changeDetectorRef: ChangeDetectorRef,
        private readonly renderer: Renderer2
    ) {
        super(elm, eventBus);
    }

    public override ngAfterViewInit(): void {
        if (this.multiline) {
            this.renderer.addClass(this.element, 'multiline-ins-textarea');
            this.renderer.addClass(this.element, 'no-wrap');
            if (!BrowserIdentifier.isFirefox()) {
                this.renderer.addClass(this.element, 'overflow-x');
            }
        }

        this.handleOverflowScrollVisible();
    }

    public override writeValue(value: string): void {
        super.writeValue(value);
        if (this.multiline) {
            const textLines = this.getLinesCount(value);
            this.setRows(textLines);
        }
    }

    private handleOverflowScrollVisible(): void {
        var sheet = new CSSStyleSheet;
        sheet.replaceSync( `textarea:disabled { overflow-y: auto !important; pointer-events: auto !important; }` );
        this.element?.shadowRoot?.adoptedStyleSheets?.push(sheet);
    }

    private setRows(value: number) {
        this.currentRowsCount = value ? value > this.rowsLimit ? this.rowsLimit : value : 1;
        this.element.setAttribute('rows', this.currentRowsCount.toString());
        this.currentRowsCount > 1 ? this.element.classList.add('multiline-no-padding') : this.element.classList.remove('multiline-no-padding');
        this.changeDetectorRef.detectChanges();
    }

    private getLinesCount(text: string): number {
        return text?.split(/\n/).length ?? 0;
    }

    public override registerOnChange(fn: (value: any) => void): void {
        super.registerOnChange(value => {
            fn(value);
        });
    }

    public override registerOnTouched(fn: () => void): void {
        super.registerOnTouched(() => {
            fn();
        });
    }

    public override handleChangeEvent(_: HTMLElement, value: string): void {
        const newValue = value?.trim() || null;
        super.handleChangeEvent(_, newValue as any);
    }

    @HostListener('insInput', ['$event.target'])
    @HostListener('insChange', ['$event.target'])
    protected handleInsInput(elem: unknown) {
        if (!this.multiline) return;
        const lines = this.getLinesCount((elem as HTMLInputElement).value);
        this.setRows(lines);
    }

    @HostListener('keydown', ['$event'])
    protected handleEnter(event: KeyboardEvent): void {
        if (event.keyCode === Keys.Enter) {
            if (this.multiline) {
                if (event.shiftKey) {
                    event.stopPropagation();
                    if (this.currentRowsCount === this.rowsLimit) return;
                    this.setRows(this.currentRowsCount + 1);
                } else {
                    this.handleBlurEvent(event.target as HTMLInputElement);
                    event.preventDefault();
                }
            } else {
                this.handleBlurEvent(event.target as HTMLInputElement);
            }
        }
    }

    @HostListener('insBlur', ['$event.target'])
    protected override handleBlurEvent(elm: unknown): void {
        if (elm === this.elm.nativeElement) {
            this.handleChangeEvent(elm as HTMLInputElement, (elm as HTMLInputElement).value);
            this.onTouched();
        }
    }
}
